import { Icon } from '@chakra-ui/react'
import { Download, RefreshCw, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactModal from 'react-modal'
import styled from 'styled-components'
import { useModalState } from '~/atoms/ModalState'
import { FontStyle } from '~/components/core/Text'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useWindowDimensions } from '~/hooks/useWindowDimensions'
import { AnalyticsNamespace, MessagingAnalyticsEvents } from '~/types/Analytics'

import { Text } from './core'

const Toolbar = styled.div`
  display: flex;
  opacity: 0;
  pointer-events: none;
  transition: opacity 150ms ease-in-out;
  position: absolute;
  padding: 21px;
  bottom: 0;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  height: 60px;
  background: rgba(0, 0, 0, 0.4);
  border-top: none;
  border-radius: 0px 0px 8px 8px;
`

const CloseButtonWrapper = styled.div`
  display: flex;
  position: absolute;
  top: 20px;
  right: 20px;
`

export const ToolbarSection = styled.div`
  display: flex;
  height: 100%;
`

const focusRing = `
  &:focus-visible {
    outline: 2px solid var(--chakra-colors-brand-contrast);
    outline-offset: 2px;
  }
`

const CloseButton = styled.button`
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  padding: 0;
  background-color: var(--chakra-colors-fg-default);
  cursor: pointer;
  ${focusRing}
`

const ToolbarButton = styled.button`
  display: flex;
  align-items: center;
  height: 100%;
  border: none;
  border-radius: 4px;
  padding: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  ${focusRing}
`

const StyledModal = styled(ReactModal)`
  box-sizing: border-box;
  overflow: hidden;
  outline: none;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover
    ${Toolbar},
    &:focus-visible
    ${Toolbar},
    ${Toolbar}:has(:focus-visible) {
    opacity: 1;
    pointer-events: auto;
  }
`

const ModalContentWrapper = styled.div<{ $width: number; $height: number }>`
  display: flex;
  width: ${(props) => props.$width}px;
  height: ${(props) => props.$height}px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--chakra-colors-bg-muted);
`

const PreviewImage = styled.img<{ $width: number; $rotation: number }>`
  margin: auto;
  width: ${(props) => props.$width}px;
  height: auto;
  transform: ${({ $rotation }) => `rotate(${$rotation}deg)`};
  image-orientation: from-image;
  user-select: none;
`

export const IconWrapper = styled.div`
  margin-left: 20px;
  display: flex;
`

const PreviewModal = () => {
  const { t } = useTranslation()
  const { height, width } = useWindowDimensions()
  const { track } = useAnalytics(AnalyticsNamespace.MESSAGING)

  const {
    previewModal: [previewModalState, setPreviewModalState],
  } = useModalState()

  const { open, previewUrl, previewFilename } = previewModalState

  const [containerWidth, setContainerWidth] = useState(width * 0.5)
  const [containerHeight, setContainerHeight] = useState(width * 0.5)

  const [imgWidth, setImgWidth] = useState(0)

  const [rotation, setRotation] = useState(0)

  const handleCloseModal = () => {
    setRotation(0)
    setImgWidth(containerWidth)
    setPreviewModalState({ open: false })
  }

  useEffect(() => {
    handleCloseModal()
  }, [])

  const handleImageRender = ({
    target: img,
  }: React.SyntheticEvent<HTMLImageElement>) => {
    const imageElement = img as HTMLImageElement
    const rate = imageElement.offsetWidth / imageElement.offsetHeight
    const scalingFactor = 1.2 // adjust this to change the size of the image
    const newImgH =
      rate > 1
        ? (width * 0.514 * scalingFactor) / rate
        : height * 0.64 * scalingFactor
    const newImgW =
      rate > 1
        ? width * 0.514 * scalingFactor
        : height * 0.64 * rate * scalingFactor
    setContainerHeight(newImgH)
    if (newImgH > newImgW) setContainerWidth(newImgH)
    else setContainerWidth(newImgW)
    setImgWidth(newImgW)
  }

  const handleDownload = async () => {
    if (!previewUrl || !previewFilename) {
      track(MessagingAnalyticsEvents.MmsPreviewDownloadMissingParams)
      return
    }

    try {
      const img = await fetch(previewUrl, {
        method: 'GET',
        headers: { 'Cache-Control': 'no-cache' },
      })
      const blob = await img.blob()
      const imgUrl = URL.createObjectURL(blob)

      const anchor = document.createElement('a')
      anchor.href = imgUrl
      anchor.download = previewFilename

      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)

      URL.revokeObjectURL(imgUrl)
    } catch {
      track(MessagingAnalyticsEvents.MmsPreviewDownloadFailed)
    }
  }

  const handleRotate = () => {
    if (containerHeight !== containerWidth) {
      if (imgWidth > containerHeight) {
        setImgWidth(containerHeight)
      } else {
        setImgWidth(containerWidth)
      }
    }
    setRotation(rotation + 90)
  }

  return (
    <StyledModal
      isOpen={open}
      ariaHideApp={false}
      onRequestClose={handleCloseModal}
      contentLabel={t('previewModal.imagePreview', 'Image preview')}
      shouldCloseOnOverlayClick
      style={{
        overlay: { backgroundColor: 'var(--chakra-colors-bg-subtle)' },
      }}
    >
      <ModalContentWrapper
        $width={containerWidth}
        $height={containerHeight}
        onContextMenu={(e) => {
          e.preventDefault()
        }}
      >
        <PreviewImage
          $width={imgWidth}
          src={previewUrl}
          alt={previewFilename ?? ''}
          onLoad={handleImageRender}
          $rotation={rotation}
        />
      </ModalContentWrapper>
      <CloseButtonWrapper>
        <CloseButton
          type="button"
          aria-label={t('global.close', 'Close')}
          onClick={handleCloseModal}
        >
          <X size={20} color="white" aria-hidden />
        </CloseButton>
      </CloseButtonWrapper>
      <Toolbar>
        <ToolbarSection>
          <ToolbarButton
            type="button"
            style={{ marginLeft: '20px' }}
            onClick={handleRotate}
          >
            <Icon aria-hidden>
              <RefreshCw size={20} />
            </Icon>
            <Text
              as="span"
              fontStyle={FontStyle.Normal}
              text={t('previewModal.rotate', 'Rotate')}
              color="brand.contrast"
              style={{
                marginTop: '3px',
                marginLeft: '8px',
                fontSize: '14px',
                userSelect: 'none',
              }}
            />
          </ToolbarButton>
        </ToolbarSection>
        <ToolbarSection>
          <ToolbarButton type="button" onClick={() => handleDownload()}>
            <Icon aria-hidden>
              <Download size={20} />
            </Icon>
            <Text
              as="span"
              fontStyle={FontStyle.Normal}
              text={t('pdfToolbar.download', 'Download')}
              color="brand.contrast"
              style={{
                marginTop: '3px',
                marginLeft: '8px',
                fontSize: '14px',
                userSelect: 'none',
              }}
            />
          </ToolbarButton>
        </ToolbarSection>
      </Toolbar>
    </StyledModal>
  )
}

export default PreviewModal
